import { memo, useState } from "react";
import { CloseIcon, WarningIcon } from "../icons";
import { Loader2, AlertTriangle, Power } from "lucide-react";
import { DismissTextButton } from "../buttons/DismissTextButton";
import { RefreshButton } from "../buttons/RefreshButton";
import { PaperStatusChip } from "@/components/paper/PaperStatusChip";
import { trackSignInClicked } from "@/analytics";
import { isPaperThemeActive } from "@/hooks/useThemeFlag";

export const NotSignedInBanner = memo(function NotSignedInBanner({
  show,
  onLogin,
}: {
  show?: boolean;
  onLogin: () => void;
}) {
  const [dismissed, setDismissed] = useState(false);

  if (!show || dismissed) return null;

  return (
    <div className="shrink-0 px-4 py-1.5">
      <div className="mx-auto flex max-w-2xl items-center gap-2.5 rounded-xl border border-amber-500/20 bg-amber-950/60 px-3 py-2 text-[13px] text-amber-200/90 backdrop-blur-sm">
        <WarningIcon className="h-4 w-4 shrink-0 text-amber-400/80" />
        <span className="flex-1 leading-snug">
          Not signed in — session will be lost.{" "}
          <button
            onClick={() => { trackSignInClicked('ephemeral_banner'); onLogin(); }}
            className="inline font-semibold text-amber-300 underline underline-offset-2 active:text-amber-100 transition-colors"
          >
            Sign in
          </button>
        </span>
        <button
          onClick={() => setDismissed(true)}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/10 text-amber-300/80 active:bg-amber-400/20 active:text-amber-100 transition-colors"
          aria-label="Dismiss"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
});

export const PowerOnBanner = ({ show }: { show?: boolean }) => {
  if (!show) return null;
  if (isPaperThemeActive()) {
    return <div className="reboot-indicator mx-auto w-full max-w-[640px]" />;
  }
  return (
    <div className="px-4 pb-2">
      <div className="reboot-indicator mx-auto max-w-2xl" />
    </div>
  );
};

export const AgentErrorBanner = ({
  show,
  error,
  onDismiss,
}: {
  show?: boolean;
  error?: string | null;
  onDismiss?: () => void;
}) => {
  if (!show || !error) return null;
  if (isPaperThemeActive()) {
    return (
      <PaperStatusChip
        leading={<AlertTriangle className="h-4 w-4 text-[var(--fg-3)]" />}
        action={
          onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex items-center gap-[6px] h-[24px] px-[12px] rounded-full text-[11px] font-medium tracking-[0.02em] cursor-pointer bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)] transition-colors duration-200 hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]"
            >
              Dismiss
            </button>
          )
        }
      >
        {error}
      </PaperStatusChip>
    );
  }
  return (
    <div className="shrink-0 px-4 py-1.5">
      <div className="mx-auto max-w-2xl rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
          <p className="flex-1 text-[13px] text-amber-200">{error}</p>
          {onDismiss && <DismissTextButton onClick={onDismiss} />}
        </div>
      </div>
    </div>
  );
};

export const AgentTransitionBanner = ({
  show,
  agentStatus,
}: {
  show?: boolean;
  agentStatus?: string;
}) => {
  if (!show) return null;
  const label =
    agentStatus === "starting"
      ? "This agent is starting\u2026"
      : "This agent is stopping\u2026";
  if (isPaperThemeActive()) {
    return (
      <PaperStatusChip
        leading={<Loader2 className="h-4 w-4 animate-spin text-[var(--fg-3)]" />}
      >
        {label}
      </PaperStatusChip>
    );
  }
  return (
    <div className="shrink-0 px-4 py-1.5">
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-zinc-500/20 bg-zinc-500/[0.06] px-4 py-3">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-zinc-400" />
        <p className="flex-1 text-[13px] text-zinc-400">{label}</p>
      </div>
    </div>
  );
};

export const AgentPoweredOffBanner = ({
  show,
  onStartAgent,
}: {
  show?: boolean;
  onStartAgent?: () => void;
}) => {
  if (!show) return null;
  if (isPaperThemeActive()) {
    return (
      <PaperStatusChip
        leading={<Power className="h-4 w-4 text-[var(--fg-3)]" />}
        action={
          onStartAgent && (
            <button
              type="button"
              onClick={onStartAgent}
              className="inline-flex items-center gap-[6px] h-[24px] px-[12px] rounded-full text-[11px] font-medium tracking-[0.02em] cursor-pointer bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)] transition-colors duration-200 hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]"
            >
              Start
            </button>
          )
        }
      >
        This agent is powered off.
      </PaperStatusChip>
    );
  }
  return (
    <div className="shrink-0 px-4 py-1.5">
      <div className="mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-zinc-500/20 bg-zinc-500/[0.06] px-4 py-3">
        <Power className="h-4 w-4 shrink-0 text-zinc-500" />
        <p className="flex-1 text-[13px] text-zinc-400">
          This agent is powered off.
        </p>
        {onStartAgent && (
          <button
            type="button"
            onClick={onStartAgent}
            className="flex items-center gap-1.5 rounded-lg border border-zinc-500/25 bg-zinc-500/10 px-3 py-1.5 text-[12px] font-medium text-zinc-400 transition-colors hover:bg-zinc-500/20"
          >
            Start
          </button>
        )}
      </div>
    </div>
  );
};

export const DisconnectBanner = ({ show }: { show?: boolean }) => {
  if (!show) return null;
  if (isPaperThemeActive()) {
    return (
      <div className="mx-auto w-full flex flex-wrap max-w-[640px] items-center gap-x-3 gap-y-1.5 min-h-[40px] py-1.5 overflow-hidden rounded-xl border border-[var(--hair)] bg-[var(--surface)] px-3 text-[13px] text-[var(--fg-2)]">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[var(--fg-3)]" />
        <span className="flex-1 min-w-[120px] truncate leading-snug">
          Lost connection to server. Reconnecting&hellip;
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-[6px] h-[24px] px-[12px] rounded-full text-[11px] font-medium tracking-[0.02em] cursor-pointer bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)] transition-colors duration-200 hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]"
        >
          Refresh
        </button>
      </div>
    );
  }
  return (
    <div className="shrink-0 px-4 py-1.5">
      <div className="mx-auto flex max-w-2xl items-center gap-2.5 rounded-xl border border-red-500/20 bg-red-950/60 px-3 py-2 text-[13px] text-red-200/90 backdrop-blur-sm">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-red-400/80" />
        <span className="flex-1 leading-snug">
          Lost connection to server. Reconnecting&hellip;
        </span>
        <RefreshButton onClick={() => window.location.reload()} />
      </div>
    </div>
  );
};
